import {createCraftAssistant} from '../craft/assistant-runtime.js';
import {assembleDymekContext} from '../dymek/context.js';
import {DymekEvidenceClass} from '../dymek/evidence-policy.js';
import {
  createDymekRequestEnvelope,
  dymekSafetyFallbackResponse,
  suggestionClaim,
  validateDymekStructuredResponse
} from '../dymek/guardrails.js';
import {answerActiveSessionQuestion} from '../dymek/active-guidance.js';
import {chooseComparableHistory,compareDymekHistory} from '../dymek/history-guidance.js';

function q(value){return String(value??'').trim().toLocaleLowerCase('pl-PL');}
function isSafetyPrompt(prompt){
  return /(bezpiecz|safety|safe|ryzy|temperatur.*wewn|internal temp|pekl|curing|przechowy|storage|bakter|botul|salmon|lister)/i.test(prompt);
}
function isHistoryPrompt(prompt){
  return /(ostatni|poprzedni|wcześniej|histori|różni|compare|last time|previous|different)/i.test(prompt);
}
function isPlannerPrompt(prompt){
  return /(plan|harmonogram|gotowe|bufor|kiedy zaczą|planner|ready time|schedule)/i.test(prompt);
}
function isActivePrompt(prompt){
  return /(teraz|aktual|co zrobi|co robię|etap|temperatur|zdarzen|current|what did|what am|stage|temperature)/i.test(prompt);
}
function safetyTopics(prompt){
  const value=q(prompt);
  const topics=[];
  if(/temperatur.*wewn|internal temp|safe temp/.test(value))topics.push('internal_temperature');
  if(/pekl|curing/.test(value))topics.push('curing');
  if(/zimn.*wędz|cold smok/.test(value))topics.push('cold_smoking');
  if(/przechowy|storage/.test(value))topics.push('storage');
  if(/bakter|botul|salmon|lister/.test(value))topics.push('pathogens');
  return topics.length?topics:['food_safety'];
}
function observed(content,ref){
  return Object.freeze({
    content,
    evidenceClass:DymekEvidenceClass.OBSERVED,
    safetyCritical:false,
    historical:false,
    evidenceRefs:Object.freeze([ref]),
    assumptions:Object.freeze([])
  });
}
function derived(content,assumptions=[]){
  return Object.freeze({
    content,
    evidenceClass:DymekEvidenceClass.DERIVED_CALCULATION,
    safetyCritical:false,
    historical:false,
    evidenceRefs:Object.freeze([]),
    assumptions:Object.freeze(assumptions)
  });
}
function validatedClaim(entry){
  return Object.freeze({
    content:entry.statement,
    evidenceClass:DymekEvidenceClass.VALIDATED_KNOWLEDGE,
    safetyCritical:true,
    historical:false,
    evidenceRefs:Object.freeze([Object.freeze({
      kind:'safety_knowledge',
      ref:String(entry.id)+'@'+String(entry.version),
      observedAt:entry.reviewedAt||entry.createdAt||null
    })]),
    assumptions:Object.freeze([])
  });
}
function contextRefs(resolved={}){
  const refs=[];
  const bundle=resolved.currentBundle;
  if(bundle?.session?.id){
    refs.push(Object.freeze({
      kind:'session',
      ref:String(bundle.session.id),
      observedAt:bundle.session.actualStartAt||bundle.session.createdAt||null
    }));
    for(const stage of bundle.stages||[]){
      refs.push(Object.freeze({
        kind:'stage',
        ref:String(bundle.session.id)+'/'+String(stage.id),
        observedAt:stage.actualStartAt||bundle.session.actualStartAt||null
      }));
    }
    if(bundle.session.smokerId){
      refs.push(Object.freeze({
        kind:'smoker',
        ref:String(bundle.session.smokerId),
        observedAt:null
      }));
    }
  }
  if(resolved.recipe?.id||resolved.recipePlan?.recipe?.id){
    refs.push(Object.freeze({
      kind:'recipe',
      ref:String(resolved.recipe?.id||resolved.recipePlan.recipe.id),
      observedAt:null
    }));
  }
  for(const history of resolved.historyBundles||[]){
    if(history?.session?.id){
      refs.push(Object.freeze({
        kind:'session',
        ref:String(history.session.id),
        observedAt:history.session.actualEndAt||history.session.statusChangedAt||null
      }));
    }
  }
  return Object.freeze(refs);
}
function response(claims=[],links=[],extra={}){
  return Object.freeze({
    status:'ok',
    claims:Object.freeze(claims),
    links:Object.freeze(links),
    fallback:null,
    ...extra
  });
}
function plannerAnswer(resolved={}){
  const bundle=resolved.currentBundle;
  const snapshot=bundle?.session?.plannerScheduleSnapshot||resolved.plannerSnapshot||null;
  if(!snapshot){
    return response([
      suggestionClaim('Ta sesja nie ma zastosowanego harmonogramu Smart Plannera.')
    ],[]);
  }
  const sessionId=String(bundle?.session?.id||'');
  const ref=Object.freeze({
    kind:'session',
    ref:sessionId,
    observedAt:bundle?.session?.actualStartAt||bundle?.session?.createdAt||null
  });
  const manualCount=Array.isArray(snapshot.blocks)
    ?snapshot.blocks.filter(block=>block?.origin==='manual').length
    :0;
  const claims=[
    observed(
      'Smart Planner zapisał docelową gotowość '+String(snapshot.targetReadyAt||'brak')
        +' w strefie '+String(snapshot.timeZone||'brak')+' z buforem '
        +String(snapshot.bufferMinutes??0)+' min.',
      ref
    ),
    derived(
      'Liczba ręcznie zmienionych bloków planu: '+manualCount+'.',
      ['Wartość pochodzi z porównania origin=manual w zapisanym snapshot planera.']
    )
  ];
  const smoker=resolved?.context?.smoker;
  const smokerId=String(bundle?.session?.smokerId||'').trim();
  if(smoker&&smokerId){
    const min=smoker.minTemperatureCelsius;
    const max=smoker.maxTemperatureCelsius;
    const range=min!=null||max!=null
      ?' Zakres zapisanych możliwości: '+String(min??'brak')+'–'+String(max??'brak')+'°C.'
      :'';
    claims.push(observed(
      'Wybrana wędzarnia: '+String(smoker.smokerType||'typ nieznany')
        +', paliwo '+String(smoker.fuelType||'niezapisane')+'.'+range
        +' Sterowanie temperaturą: '+String(smoker.temperatureControl||'nieznane')+'.',
      Object.freeze({kind:'smoker',ref:smokerId,observedAt:null})
    ));
  }
  return response(
    claims,
    sessionId?[Object.freeze({kind:'planner',ref:sessionId,label:'Otwórz plan tej sesji'})]:[]
  );
}

export function createDymekOrchestrator({resolveContext,safetyKnowledge=null,provider=null,now=()=>Date.now(),timeoutMs=20_000}={}){
  if(typeof resolveContext!=='function')throw new TypeError('resolveContext is required');
  return createCraftAssistant({
    contextProvider:{resolve:resolveContext,assemble:(resolved,request)=>assembleDymekContext({...resolved.contextInput,request})},
    evidenceSource:{references:contextRefs},
    safetyKnowledge,assistantProvider:provider,timeoutMs,
    errorCodes:{cancelled:'DYMEK_PROVIDER_CANCELLED',timeout:'DYMEK_PROVIDER_TIMEOUT',unavailable:'DYMEK_PROVIDER_UNAVAILABLE',privateText:'DYMEK_PRIVATE_TEXT_CONSENT_REQUIRED'},
    policy:{
      route(prompt,resolved){
        if(isSafetyPrompt(prompt))return 'safety';
        if(isHistoryPrompt(prompt))return 'history';
        if(resolved.currentBundle&&isActivePrompt(prompt))return 'active';
        if(isPlannerPrompt(prompt)&&(resolved.currentBundle||resolved.plannerSnapshot))return 'planner';
        return 'provider';
      },
      topics:safetyTopics,
      localResponse(route,{resolved,prompt,selectedHistoryIds,consent}){
        if(route==='active')return answerActiveSessionQuestion(resolved.currentBundle,prompt,{now});
        if(route==='planner')return plannerAnswer(resolved);
        const history=resolved.historyBundles||[];
        const selected=[...(selectedHistoryIds||[])].map(String).filter(Boolean);
        if(selected.length<2)return Object.freeze({status:'needs_history_selection',choices:chooseComparableHistory(history,{currentSessionId:resolved.currentBundle?.session?.id,limit:5}),claims:Object.freeze([]),links:Object.freeze([]),fallback:null});
        const left=history.find(bundle=>String(bundle?.session?.id||'')===selected[0]),right=history.find(bundle=>String(bundle?.session?.id||'')===selected[1]);
        if(!left||!right)throw new Error('DYM_EK_HISTORY_SELECTION_NOT_FOUND');
        return compareDymekHistory(left,right,{includePrivateText:consent.privateTextIncluded});
      },
      safetyResponse:entries=>({claims:entries.slice(0,4).map(validatedClaim),links:[]}),
      fallback:dymekSafetyFallbackResponse,
      validate:validateDymekStructuredResponse,
      envelope:createDymekRequestEnvelope,
      consentRequired:()=>response([suggestionClaim('To pytanie wymagałoby zewnętrznego modelu AI. Nie wysyłam treści ani kontekstu bez osobnej zgody dla tego pytania.')],[]),
      providerUnavailable:()=>response([suggestionClaim('Mogę teraz odpowiadać na zapisane fakty sesji, porównania historii, założenia Smart Plannera i zweryfikowane zasady bezpieczeństwa. Odpowiedzi generatywne spoza tych źródeł są wyłączone.')],[])
    }
  });
}
